import React, { useMemo, useState } from 'react';
import PackageFeatureLibraryPanel from '../../pages/packages/feature-library/PackageFeatureLibraryPanel';
import { buildFeatureLibrary, PACKAGE_FEATURE_DRAG_TYPE } from '../../pages/packages/feature-library/collectPackageFeatures';
import '../../pages/packages/package-form-page.css';
import './custom-plan-form.css';

const AddIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

export default function CustomPlanFormFields({
  formData,
  setFormData,
  allPackages = [],
  readOnly = false,
}) {
  const [featureLibraryOpen, setFeatureLibraryOpen] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [featuresDragOver, setFeaturesDragOver] = useState(false);

  const libraryGroups = useMemo(
    () => buildFeatureLibrary(allPackages, { currentFeatures: formData.features }),
    [allPackages, formData.features]
  );

  const appendFeature = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    setFormData((prev) => {
      if (prev.features.some((f) => f.toLowerCase() === trimmed.toLowerCase())) {
        return prev;
      }
      return { ...prev, features: [...prev.features, trimmed] };
    });
    setNewFeature('');
  };

  const handleLibrarySelect = (featureText) => {
    appendFeature(featureText);
    setFeatureLibraryOpen(false);
  };

  const handleLibraryDrop = (event, index) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData(PACKAGE_FEATURE_DRAG_TYPE);
    if (raw) {
      appendFeature(raw);
      return;
    }
    const fromIndex = parseInt(event.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === index) return;
    setFormData((prev) => {
      const next = [...prev.features];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(index, 0, moved);
      return { ...prev, features: next };
    });
  };

  return (
    <div className="cp-form">
      <div className="cp-form__grid">
        <label className="cp-field">
          <span>Customer label *</span>
          <input
            type="text"
            value={formData.customerLabel}
            disabled={readOnly}
            onChange={(e) => setFormData((p) => ({ ...p, customerLabel: e.target.value }))}
            placeholder="Acme Corp"
          />
        </label>
        <label className="cp-field">
          <span>Plan display name *</span>
          <input
            type="text"
            value={formData.planName}
            disabled={readOnly}
            onChange={(e) => setFormData((p) => ({ ...p, planName: e.target.value }))}
          />
        </label>
        <label className="cp-field">
          <span>Monthly price (USD) *</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.monthlyPrice}
            disabled={readOnly}
            onChange={(e) => setFormData((p) => ({ ...p, monthlyPrice: e.target.value }))}
          />
        </label>
        <label className="cp-field">
          <span>Billing cycle</span>
          <select
            value={formData.billingCycle}
            disabled={readOnly}
            onChange={(e) => setFormData((p) => ({ ...p, billingCycle: e.target.value }))}
          >
            <option value="monthly">Monthly</option>
            <option value="annually">Annual</option>
          </select>
        </label>
        <label className="cp-field">
          <span>Annual discount (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            value={formData.annualDiscount}
            disabled={readOnly}
            onChange={(e) => setFormData((p) => ({ ...p, annualDiscount: e.target.value }))}
          />
        </label>

        <div className="cp-field cp-field--employees">
          <span>Employees</span>
          <div className="cp-employees-row">
            <label className="cp-checkbox">
              <input
                type="checkbox"
                checked={formData.unlimitedEmployees}
                disabled={readOnly}
                onChange={(e) => setFormData((p) => ({ ...p, unlimitedEmployees: e.target.checked }))}
              />
              Unlimited employees
            </label>
            {!formData.unlimitedEmployees && (
              <label className="cp-employees-limit">
                <span className="cp-employees-limit__label">Limit</span>
                <input
                  type="number"
                  min="1"
                  value={formData.employeeLimit}
                  disabled={readOnly}
                  onChange={(e) => setFormData((p) => ({ ...p, employeeLimit: e.target.value }))}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="cp-features">
        <div className="cp-features__header">
          <h3>Features</h3>
          {!readOnly && (
            <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setFeatureLibraryOpen(true)}>
              Feature library
            </button>
          )}
        </div>
        {!readOnly && (
          <div className="cp-feature-add">
            <input
              type="text"
              value={newFeature}
              placeholder="Add a feature"
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  appendFeature(newFeature);
                }
              }}
            />
            <button type="button" className="cp-btn" onClick={() => appendFeature(newFeature)}>
              <AddIcon /> Add
            </button>
          </div>
        )}
        <ul
          className={`cp-feature-list${featuresDragOver ? ' cp-feature-list--drag-over' : ''}`}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes(PACKAGE_FEATURE_DRAG_TYPE)) {
              e.preventDefault();
              setFeaturesDragOver(true);
            }
          }}
          onDragLeave={() => setFeaturesDragOver(false)}
        >
          {formData.features.map((feature, index) => (
            <li
              key={`${feature}-${index}`}
              draggable={!readOnly}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', String(index));
              }}
              onDrop={(e) => {
                handleLibraryDrop(e, index);
                setFeaturesDragOver(false);
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <span>{feature}</span>
              {!readOnly && (
                <button
                  type="button"
                  className="cp-icon-btn"
                  aria-label="Remove feature"
                  onClick={() => setFormData((p) => ({
                    ...p,
                    features: p.features.filter((_, i) => i !== index),
                  }))}
                >
                  <DeleteIcon />
                </button>
              )}
            </li>
          ))}
          {formData.features.length === 0 && (
            <li className="cp-feature-list__empty">Add at least one feature before sending an invite.</li>
          )}
        </ul>
      </div>

      <label className="cp-field">
        <span>Customer-facing notes (optional)</span>
        <textarea
          rows={2}
          value={formData.customerNotes}
          disabled={readOnly}
          onChange={(e) => setFormData((p) => ({ ...p, customerNotes: e.target.value }))}
          placeholder="Shown on the invitation accept page"
        />
      </label>
      <label className="cp-field">
        <span>Internal notes (admin only)</span>
        <textarea
          rows={2}
          value={formData.internalNotes}
          disabled={readOnly}
          onChange={(e) => setFormData((p) => ({ ...p, internalNotes: e.target.value }))}
        />
      </label>

      <PackageFeatureLibraryPanel
        open={featureLibraryOpen}
        onClose={() => setFeatureLibraryOpen(false)}
        libraryGroups={libraryGroups}
        onSelectFeature={handleLibrarySelect}
        onAddCustomFeature={() => setFeatureLibraryOpen(false)}
      />
    </div>
  );
}
